import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Paper, Radio, RadioGroup, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, sectionCardSx, listCardSx, insetPanelSx, sectionHeadingSx, emptyTextSx,
  actionRowSx, dialogProps, dialogTitleSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from '../securityhub/OperationProgress';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import {
  deleteStackSet, getStackSet, getStackSetTemplate, listInstances, listOperations,
  retryInstances, stopOperation, syncStackSet,
} from '../../../services/stacksetsApi';
import { statusColor } from './StackSetsList';
import { formatStatusLabel } from '../../../utils/statusLabel';
import InstanceEventsRow from './InstanceEventsRow';

const ACTIVE = ['in_progress', 'updating', 'deleting'];

// Admin-console tab styling: Metropolis 600, sentence case, accent on select.
const tabSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  minHeight: 56,
  color: CK.body,
  '&.Mui-selected': { color: CK.blue },
};

const tabsSx = {
  borderBottom: `1px solid ${CK.border}`,
  px: 2,
  '& .MuiTabs-indicator': { backgroundColor: CK.blue, height: 3 },
};

const dialogBodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

// Sub-label for the two groups inside the Targets section. Deliberately quieter
// than sectionHeadingSx: "Targets" is the section, OUs and accounts are its parts.
const targetGroupLabelSx = {
  fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 600, color: CK.body, mb: 0.5,
};

export default function StackSetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [dep, setDep] = useState(null);
  const [instances, setInstances] = useState([]);
  const [operations, setOperations] = useState([]);
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [error, setError] = useState(null); // page-blocking: refresh() failures only
  const [actionError, setActionError] = useState(null); // per-action failures — dismissible banner
  const [notice, setNotice] = useState(null);
  const pollRef = useRef(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [retain, setRetain] = useState('delete');
  const [confirmText, setConfirmText] = useState('');
  // Sync dialog state
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncRetain, setSyncRetain] = useState('delete');

  const refresh = useCallback(async () => {
    try {
      const [d, i, o] = await Promise.all([getStackSet(id), listInstances(id), listOperations(id)]);
      setDep(d?.data || null);
      setInstances(i?.data || []);
      setOperations(o?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll keyed on parent status (frozen-bar lesson: NOT on cell activity).
  useEffect(() => {
    if (!ACTIVE.includes(dep?.status) || pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      await refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [dep?.status, refresh]);

  // Stop the interval inside the tick when the parent settles.
  useEffect(() => {
    if (dep && !ACTIVE.includes(dep.status) && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [dep]);

  // Lazy template fetch. On failure, template stays null (so Retry re-runs
  // this) and templateError is surfaced in the tab; the in-flight flag
  // prevents double fetches from rapid tab clicks.
  const loadTemplate = useCallback(async () => {
    if (template !== null || loadingTemplate) return;
    setLoadingTemplate(true);
    setTemplateError(null);
    try {
      const res = await getStackSetTemplate(id);
      setTemplate(res?.data?.templateBody || '');
    } catch (e) {
      setTemplateError(e.message);
    } finally {
      setLoadingTemplate(false);
    }
  }, [id, template, loadingTemplate]);

  // Error and loading keep the page header mounted so only the card body under
  // it changes when the StackSet lands.
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title={dep?.name || 'StackSet'}
            subtitle={dep?.description || 'CloudFormation StackSet'}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={refresh} />
        </motion.div>
      </motion.div>
    );
  }
  if (!dep) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<LayersIcon sx={{ fontSize: 24 }} />}
            title="StackSet"
            subtitle="CloudFormation StackSet"
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSet…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  const parse = (raw, fallback) => { try { return JSON.parse(raw || ''); } catch { return fallback; } };
  // Target lists are read through parseList, never parse, because "absent" reaches
  // this page in two shapes: a missing/empty column (parse's catch fallback fires)
  // and a Go nil slice, which marshals to the literal `null` — and JSON.parse('null')
  // SUCCEEDS, so the catch never fires and `.length`/`.map` would hit null. Both
  // sides need it: a StackSet created before accounts were targetable has no
  // targetAccounts, and an accounts-only StackSet stores no OUs.
  const parseList = (raw) => { const v = parse(raw, []); return Array.isArray(v) ? v : []; };
  const ous = parseList(dep.targetOus);
  const accounts = parseList(dep.targetAccounts);
  const regions = parse(dep.regions, []);
  const parameters = parse(dep.parameters, []);
  const failedCells = instances.filter((r) => r.phase === 'failed');
  const runningOp = operations.find((o) => o.status === 'running');
  const editDisabled = ACTIVE.includes(dep.status) || dep.status === 'deleted' || !!runningOp;

  const doDelete = async () => {
    try {
      await deleteStackSet(id, retain === 'retain');
      setDeleteOpen(false);
      refresh();
    } catch (e) {
      setActionError(e.message);
      setDeleteOpen(false);
    }
  };

  const openSync = async () => {
    try {
      const res = await syncStackSet(id, { dryRun: true });
      setSyncPlan(res?.data || { addCells: [], removeCells: [], flaggedAccounts: [] });
    } catch (e) {
      setActionError(e.message);
    }
  };

  const runSync = async () => {
    try {
      await syncStackSet(id, { dryRun: false, retainStacks: syncRetain === 'retain' });
      setSyncPlan(null);
      setNotice('Sync started');
      refresh();
    } catch (e) {
      setActionError(e.message);
      setSyncPlan(null);
    }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LayersIcon sx={{ fontSize: 24 }} />}
          title={dep.name}
          subtitle={dep.description || 'CloudFormation StackSet'}
        />
      </motion.div>
      {notice && (
        <motion.div variants={sectionVariants}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setNotice(null)}>{notice}</Alert>
        </motion.div>
      )}
      {actionError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setActionError(null)}>{actionError}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Box sx={{ ...actionRowSx, mb: 2 }}>
          <Chip size="small" label={formatStatusLabel(dep.status)} sx={statusChipSx(intentFromColor(statusColor(dep.status)))} />
          <Button size="small" variant="outlined" disabled={editDisabled} onClick={() => navigate(`/stacksets/${id}/edit`)}>
            Edit
          </Button>
          {ACTIVE.includes(dep.status) && (
            <Box sx={{ flex: '1 1 260px', minWidth: 0, maxWidth: 420 }}>
              <OperationProgress percent={dep.progress} label={dep.progressLabel || 'Working…'} />
            </Box>
          )}
          {dep.errorMessage && (
            <Alert severity="warning" sx={{ flex: '1 1 260px', minWidth: 0, borderRadius: '8px' }}>
              {dep.errorMessage}
            </Alert>
          )}
        </Box>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); if (v === 3) loadTemplate(); }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={tabsSx}
          >
            <Tab label="Overview" sx={tabSx} />
            <Tab label={`Instances (${instances.length})`} sx={tabSx} />
            <Tab label="Operations" sx={tabSx} />
            <Tab label="Template" sx={tabSx} />
            <Tab label="Settings" sx={tabSx} />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* OUs and individual accounts are both targetable, so this section
                  shows whichever of the two the StackSet actually uses — a group
                  with nothing in it is omitted rather than shown as a zero, and
                  the empty state only appears when BOTH are empty. */}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Targets</Typography>
              <Box sx={{ mb: 3 }}>
                {ous.length === 0 && accounts.length === 0 && (
                  <Typography sx={emptyTextSx}>No targets.</Typography>
                )}
                {ous.length > 0 && (
                  <Box sx={{ mb: accounts.length > 0 ? 2 : 0 }}>
                    <Typography sx={targetGroupLabelSx}>Organizational units</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {ous.map((ou) => <Chip key={ou.id} size="small" label={`${ou.name} (${ou.id})`} sx={statusChipSx('info')} />)}
                    </Box>
                  </Box>
                )}
                {accounts.length > 0 && (
                  <Box>
                    <Typography sx={targetGroupLabelSx}>Accounts</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {accounts.map((a) => <Chip key={a.id} size="small" label={a.name ? `${a.name} (${a.id})` : a.id} sx={statusChipSx('success')} />)}
                    </Box>
                  </Box>
                )}
              </Box>

              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Regions</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {regions.length === 0 && <Typography sx={emptyTextSx}>No regions selected.</Typography>}
                {regions.map((r) => <Chip key={r} size="small" label={r} sx={statusChipSx('neutral')} />)}
              </Box>

              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Parameters</Typography>
              <TableContainer sx={{ maxWidth: 560 }}>
                <Table size="small">
                  <TableBody>
                    {parameters.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={emptyTextSx}>This template takes no parameters.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {parameters.map((p, index) => (
                      <TableRow key={p.key} hover sx={rowSx(index)}>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.key}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{p.noEcho ? '••••••••' : p.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', mt: 3 }}>
                Template v{dep.templateVersion} · Created by {dep.createdByEmail} · {new Date(dep.createdAt).toLocaleString()}
              </Typography>
            </Box>
          )}

          {tab === 1 && (
            <>
              {failedCells.length > 0 && (
                <Box sx={{ ...actionRowSx, p: 2, borderBottom: `1px solid ${CK.border}` }}>
                  <Button variant="outlined" color="warning" onClick={async () => {
                    try { await retryInstances(id); setNotice('Retry started'); refresh(); } catch (e) { setActionError(e.message); }
                  }}>
                    Retry {failedCells.length} failed instance{failedCells.length === 1 ? '' : 's'}
                  </Button>
                </Box>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      <TableCell sx={{ ...headCellSx, width: 40 }} />
                      <TableCell sx={headCellSx}>Account</TableCell><TableCell sx={headCellSx}>Region</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell><TableCell sx={headCellSx}>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instances.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={emptyTextSx}>
                            No stack instances yet — they appear once the StackSet deploys to your target accounts.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {instances.map((r, index) => (
                      <InstanceEventsRow key={r.id} instance={r} stackSetId={id} index={index} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tab === 2 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>Type</TableCell><TableCell sx={headCellSx}>Status</TableCell><TableCell sx={headCellSx}>Requested by</TableCell>
                    <TableCell sx={headCellSx}>Started</TableCell><TableCell sx={headCellSx}>Completed</TableCell><TableCell sx={headCellSx} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No operations yet — deployments, updates and syncs are recorded here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {operations.map((o, index) => (
                    <TableRow key={o.id} hover sx={rowSx(index)}>
                      <TableCell sx={bodyCellSx}>{formatStatusLabel(o.type)}</TableCell>
                      <TableCell sx={bodyCellSx}><Chip size="small" label={formatStatusLabel(o.status)} sx={statusChipSx(intentFromColor(o.status === 'succeeded' ? 'success' : o.status === 'running' ? 'info' : o.status === 'partial' ? 'warning' : 'error'))} /></TableCell>
                      <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{o.requestedByEmail}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{new Date(o.startedAt).toLocaleString()}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{o.completedAt ? new Date(o.completedAt).toLocaleString() : '—'}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }} align="right">
                        {o.status === 'running' && o.awsOperationId && (
                          <Button size="small" color="warning" onClick={async () => {
                            try { await stopOperation(id, o.id); setNotice('Stop requested'); refresh(); } catch (e) { setActionError(e.message); }
                          }}>Stop</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <Box sx={{ p: 3 }}>
              {templateError ? (
                <Alert
                  severity="error"
                  sx={{ borderRadius: '8px' }}
                  action={<Button color="inherit" size="small" onClick={loadTemplate}>Retry</Button>}
                >
                  Failed to load template: {templateError}
                </Alert>
              ) : template === null ? <PortalLoading label="Loading template…" /> : (
                <Box
                  component="pre"
                  sx={{
                    ...insetPanelSx,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    overflow: 'auto',
                    maxHeight: 480,
                    m: 0,
                  }}
                >
                  {template}
                </Box>
              )}
              <Button variant="outlined" sx={{ mt: 2 }} disabled={editDisabled} onClick={() => navigate(`/stacksets/${id}/edit`)}>
                Update template
              </Button>
            </Box>
          )}

          {tab === 4 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Target sync</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
                Accounts added to (or removed from) the target OUs after deployment are not
                handled automatically — run Sync to reconcile. An account you targeted
                explicitly is never removed by Sync: if it stops qualifying, its stacks are
                kept and the account is reported so you can decide.
              </Typography>
              <Button variant="outlined" disabled={ACTIVE.includes(dep.status)} onClick={openSync}>Preview sync</Button>

              <Typography sx={{ ...sectionHeadingSx, mt: 4, mb: 1 }}>Danger zone</Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
                Deleting a StackSet removes it from Prism and, unless you retain them, destroys
                its stacks in every targeted account and region.
              </Typography>
              <Button variant="outlined" color="error" disabled={ACTIVE.includes(dep.status) || dep.status === 'deleted' || !!runningOp} onClick={() => setDeleteOpen(true)}>
                Delete StackSet
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* Delete dialog: AWS parity — Delete vs Retain stacks + type-to-confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Delete StackSet “{dep.name}”</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={retain} onChange={(e) => setRetain(e.target.value)} sx={{ mt: 1 }}>
            <FormControlLabel value="delete" control={<Radio />} label="Delete stacks — destroys the template's resources in every account/region" />
            <FormControlLabel value="retain" control={<Radio />} label="Retain stacks — deployed resources stay (and keep billing) in member accounts" />
          </RadioGroup>
          {retain === 'delete' && (
            <Alert severity="error" sx={{ my: 1, borderRadius: '8px' }}>
              All resources created by this template will be permanently destroyed.
            </Alert>
          )}
          <TextField
            fullWidth size="small" sx={{ mt: 1 }}
            label={`Type ${dep.name} to confirm`}
            value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={confirmText !== dep.name} onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Sync dialog: shows the add/remove diff before executing */}
      <Dialog open={!!syncPlan} onClose={() => setSyncPlan(null)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Sync targets</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Optional chaining on every list: the backend marshals a nil slice to
              JSON `null`, so a present-but-partial plan would otherwise throw here
              and take the whole Detail page down, not just this dialog. */}
          {syncPlan && !syncPlan.addCells?.length && !syncPlan.removeCells?.length
            && !syncPlan.flaggedAccounts?.length && (
            <Alert severity="success" sx={{ mt: 1, borderRadius: '8px' }}>Everything is in sync — no changes needed.</Alert>
          )}
          {syncPlan?.flaggedAccounts?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1, borderRadius: '8px' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                Kept, not removed ({syncPlan.flaggedAccounts.length})
              </Typography>
              {syncPlan.flaggedAccounts.map((f) => (
                <Typography key={f.accountId} sx={dialogBodyTextSx}>
                  {f.name ? `${f.name} (${f.accountId})` : f.accountId} — {f.reason}
                </Typography>
              ))}
              <Typography sx={{ ...dialogBodyTextSx, mt: 0.5 }}>
                These accounts are targeted explicitly, so their stacks are left in place. Remove them from
                the StackSet&apos;s targets if you want their stacks deleted.
              </Typography>
            </Alert>
          )}
          {syncPlan?.addCells?.length > 0 && (
            <>
              <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mt: 2, mb: 1 }}>
                Will deploy ({syncPlan.addCells.length})
              </Typography>
              {syncPlan.addCells.map((c) => (
                <Typography key={`${c.accountId}-${c.region}`} sx={dialogBodyTextSx}>{c.accountId} · {c.region}</Typography>
              ))}
            </>
          )}
          {syncPlan?.removeCells?.length > 0 && (
            <>
              <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mt: 2, mb: 1 }}>
                Will remove ({syncPlan.removeCells.length})
              </Typography>
              {syncPlan.removeCells.map((c) => (
                <Typography key={`${c.accountId}-${c.region}`} sx={dialogBodyTextSx}>{c.accountId} · {c.region}</Typography>
              ))}
              <RadioGroup value={syncRetain} onChange={(e) => setSyncRetain(e.target.value)} sx={{ mt: 1 }}>
                <FormControlLabel value="delete" control={<Radio />} label="Delete the removed accounts' stacks" />
                <FormControlLabel value="retain" control={<Radio />} label="Retain the removed accounts' stacks" />
              </RadioGroup>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSyncPlan(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!syncPlan || (!syncPlan.addCells?.length && !syncPlan.removeCells?.length)}
            onClick={runSync}
          >
            Sync now
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
